import React, {useRef} from 'react';
import classNames from 'classnames';
import {Link} from '../Link.jsx';
import {Picture} from '../Picture.jsx';
import {TextColor, TextPosition} from '../text-modifiers';
import {CallToAction} from '../CallToAction.jsx';
import {PriceTag} from '../PriceTag.jsx';
import {TrackingContextAppend} from '../TrackingContext.jsx';
import {CfEntry} from '../CfPreviewContext.jsx';
import {LinkBarV2} from './LinkBarV2.jsx';
import {XRay} from '@teaser/scripts/components/XRay.jsx';
import {InfoI} from "@teaser/scripts/components/InfoI.jsx";
import {Video2} from "@teaser/scripts/components/Video2.jsx";
import {VideoControls} from "@teaser/scripts/components/VideoControls.jsx";
import {useBreakpointCurrent} from 'bonprix-design-system';
import {TeaserOverlay} from '@teaser/scripts/components/TeaserOverlay.jsx';

// arbitrary aspect ratios, gained empirically, to align height of the portrait teaser
// with height of other teasers inside the grid
const getPortraitAspectRatio = (currentBreakpoint, isExpandedLayout) => {
    switch (currentBreakpoint) {
        case 's':
            return 0.47;
        case 'm':
            if (isExpandedLayout) {
                return 0.714;
            }
            return 0.48;
        case 'l':
            return 0.48;
        case 'xl':
            return 0.53;
        case 'xxl':
            return 0.55;
        case 'xxxl':
            return 0.57;
        default:
            return 0.714;
    }
};

/**
 * @typedef {TeaserLinkModel} PortraitGridTeaserModel
 * @property {'PORTRAIT_GRID_TEASER'} type
 * @property {string | null} headline
 * @property {string | null} subline
 * @property {string} backgroundColor
 * @property {ImageModel | null} backgroundImage
 * @property {VideoModel | null} video
 * @property {string | null} videoDefaultSrc
 * @property {string | null} videoDesktopSrc
 * @property {string | null} callToAction
 * @property {TextPosition} textPosition
 * @property {TextColor} textColor
 * @property {PriceTagModel} priceTags
 * @property {import('@teaser/scripts/components/TeaserOverlay.jsx').TeaserOverlayModel | null} overlay
 * @property {LinkBarV2} linkBar
 * @property {string} contentType
 * @property {string | null} mobileExpandedLayout
 * @property {String} infoHeadline;
 * @property {String} infoText;
 * @property {boolean} infoTextOnTheLeft;
 * @property {boolean} useTextShadow
 * @property {string} entryId
 * @property {Device[]} devices
 * @property {number} getSizeForArticleGrid
 */

/**
 * @typedef {import('./ArticleOverviewTeaserContent.jsx').Device} Device
 */

/**
 * @param {PortraitGridTeaserModel} model
 * @param {Device} device
 * @param {Ref<HTMLDivElement> | undefined} ref
 * @returns {JSX.Element}
 */
export const PortraitGridTeaser = React.forwardRef(function PortraitGridTeaser({model}, ref) {
    const videoRef = useRef(null);
    const breakpoint = useBreakpointCurrent();
    // On mobile (s/m) always render portrait style regardless of the CMS isPortrait flag,
    // because the single-column grid cell is too narrow for a square image.
    const isExpandedLayout = model.mobileExpandedLayout === '4';
    const textPosition = TextPosition[model.textPosition];
    const isTextAbove = textPosition === TextPosition.TOP;
    const textPositionClass = isTextAbove ? textPosition : TextPosition.BOTTOM;
    const aspectRatio = getPortraitAspectRatio(breakpoint, isExpandedLayout);
    const imageSizes = {
            xlarge: 575,
            large: 360,
            medium: 360,
            small: 270,
        }
    // Let the CSS grid row height equalise all adjacent teasers.
    // aspectRatio is passed as a CSS custom property so the video element can lock
    // its own shape — preventing the poster→playing reflow when the video file's
    // intrinsic dimensions differ from the poster crop.
    const containerStyle = {
        '--portrait-grid-background-color': model.backgroundColor,
        '--portrait-grid-aspect-ratio': aspectRatio,
    };
    // Map the current breakpoint label to the image size already calculated for that column width.
    const breakpointToSizeKey = { s: 'small', m: 'medium', l: 'large', xl: 'xlarge', xxl: 'xxlarge', xxxl: 'xxlarge' };
    const posterWidth = imageSizes[breakpointToSizeKey[breakpoint]] ?? imageSizes.xlarge ?? imageSizes.small ?? 480;
    const posterHeight = Math.round(posterWidth / aspectRatio);
    const imageArea = (
        <div className="content-platform__teaser--portrait-grid__image-area">
            {model.videoModel ? (
                <Video2 model={model} videoRef={videoRef} posterWidth={posterWidth} posterHeight={posterHeight}
                        xlarge={960}>
                    <VideoControls videoRef={videoRef} />
                </Video2>
            ) : model.backgroundImage ? (
                <div className="content-platform__teaser__img-container">
                    <Picture model={model.backgroundImage} xxlarge={imageSizes.xxlarge} xlarge={imageSizes.xlarge}
                             large={imageSizes.large} medium={imageSizes.medium} small={imageSizes.small}
                             aspectRatio={aspectRatio} />
                    {model.callToAction && <div className="content-platform__teaser--portrait-grid__cta-on-image">
                        <CallToAction text={model.callToAction} />
                    </div>}
                </div>
            ) : (
                <div className="content-platform__portrait-grid-teaser__spacer">&nbsp;</div>
            )}
            {model.videoModel && model.callToAction && <div className="content-platform__teaser--portrait-grid__cta-on-image">
                <CallToAction text={model.callToAction} />
            </div>}
        </div>
    );
    const textContent = (model.headline || model.subline) && (
            <div className={classNames('content-platform__teaser__main', TextColor[model.textColor], textPositionClass,
                {'-text-shadow': model.useTextShadow})}>
                <span className="content-platform__teaser__title_link">
                    <Headline textColor={model.textColor} text={model.headline} useTextShadow={model.useTextShadow} />
                </span>
                <Subline textColor={model.textColor} text={model.subline} useTextShadow={model.useTextShadow} />
            </div>
    );

    return (
        <TrackingContextAppend value={{id: model.entryId}}>
            <CfEntry entryId={model.entryId} contentType={model.contentType}>
                <article className={classNames(
                    'content-platform__teaser',
                    'content-platform__teaser--portrait-grid',
                    {'content-platform__teaser--portrait-grid--with-text': Boolean(model.headline || model.subline)},
                    {'content-platform__teaser--portrait-grid--text-above': isTextAbove},
                )}>
                    <XRay fieldId="internalName" />
                    <div
                        className="content-platform__teaser__container content-platform__teaser--portrait-grid__container"
                        style={containerStyle}
                        ref={ref}
                        data-teaser-id={model.entryId}
                    >
                        <Link model={model} className="absolute-link" parentEntryId={model.entryId} />
                        {isTextAbove ? <>{textContent}{imageArea}</> : <>{imageArea}{textContent}</>}
                        {model.priceTags.map((priceTag, idx) => (
                            <PriceTag
                                key={priceTag.id || priceTag.code || idx}
                                model={priceTag}
                                parentEntryId={model.entryId}
                            />
                        ))}
                        <TeaserOverlay model={model.overlay} />
                    </div>
                    {model.infoText && <InfoI model={model} />}
                    {model.linkBar && <LinkBarV2 model={model.linkBar} parentEntryId={model.entryId} />}
                </article>
            </CfEntry>
        </TrackingContextAppend>
    );
})

function Headline({text, fieldId = 'headline'}) {
    if (!text) return null;
    return (
        <p className='content-platform__teaser__headline'>
            {text}
            <XRay fieldId={fieldId}/>
        </p>
    );
}

function Subline({text, fieldId = 'subline'}) {
    if (!text) return null;
    return (
        <p className='content-platform__teaser__subline'>
            {text}
            <XRay fieldId={fieldId}/>
        </p>
    );
}
